import { Add, Close, Delete as DeleteIcon } from "@mui/icons-material"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormHelperText,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useEffect, useRef, useState } from "react"
import { Modal } from "@/core/components"
import { enqueueSnackbar } from "notistack"
import UrlButtons, {
  UrlButtonsHandle,
} from "@/modules/postBot/components/posts/postField/urlButtons"
import { ADV, ReplyMarkup } from "../../interfaces"
import { useCampaignsListQuery, useCreateContentMutation } from "../../store"
import {
  CONTENT_ACCEPT,
  hasContentDraftErrors,
  validateContentButtonFields,
  validateContentDraft,
  validateReplyMarkup,
  validateReplyMarkupLayout,
} from "../../utils/validateContent"
import {
  contentErrorMessage,
  normalizeContentError,
} from "../../utils/contentErrors"
import { formatBytes } from "../../utils/contentsFormatting"
import { AssetPickerDialog, IPickedAsset } from "./assetPickerDialog"
import { OrderPickerField } from "./orderPicker"
import { useAdvWriteAccess } from "../../hooks/useAdvWriteAccess"

export interface ICreateContentDialogProps {
  open: boolean
  onClose: () => void
  /** Предцелевая привязка из карточки ордера (FR-27): чип «Ордер #id», в order_ids атомарно. */
  presetOrderBinding?: number
}

type ReusedAsset = IPickedAsset

// Мультивыбор кампаний из campaigns/list (GET campaigns/list уже есть в adv2).
const AutocompleteMultipleCampaigns: FC<{
  campaigns: ADV.Campaign[]
  value: number[]
  onChange: (ids: number[]) => void
  disabled: boolean
}> = ({ campaigns, value, onChange, disabled }) => (
  <Autocomplete
    disabled={disabled}
    multiple
    options={campaigns}
    getOptionLabel={(option: ADV.Campaign) => option.name}
    isOptionEqualToValue={(option, valueItem) => option.id === valueItem.id}
    value={campaigns.filter((campaign) => value.includes(campaign.id))}
    onChange={(_, values) => onChange(values.map((v) => v.id))}
    renderInput={(params) => (
      <TextField {...params} label="Кампании" variant="outlined" />
    )}
  />
)

export const CreateContentDialog: FC<ICreateContentDialogProps> = ({
  open,
  onClose,
  presetOrderBinding,
}) => {
  const canWrite = useAdvWriteAccess()
  const canWriteRef = useRef(canWrite)
  canWriteRef.current = canWrite
  const [text, setText] = useState("")
  const [newFiles, setNewFiles] = useState<File[]>([])
  const [reusedAssets, setReusedAssets] = useState<ReusedAsset[]>([])
  const [markup, setMarkup] = useState<ReplyMarkup | null>(null)
  const [buttonsResetKey, setButtonsResetKey] = useState(0)
  const [campaignIds, setCampaignIds] = useState<number[]>([])
  const [orderIds, setOrderIds] = useState<number[]>([])
  const [draftErrors, setDraftErrors] = useState<ReturnType<
    typeof validateContentDraft
  > | null>(null)
  const [markupError, setMarkupError] = useState<string | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [submitMessage, setSubmitMessage] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const markupSectionRef = useRef<HTMLDivElement>(null)
  const buttonsRef = useRef<UrlButtonsHandle>(null)
  const focusMarkupRef = useRef(false)

  const { data: campaigns } = useCampaignsListQuery({}, { skip: !open })
  const [createContent, { isLoading: isCreating }] = useCreateContentMutation()
  const isSubmitting = isCreating
  const resolvedOrderIds =
    presetOrderBinding !== undefined ? [presetOrderBinding] : orderIds

  useEffect(() => {
    if (!open) {
      setText("")
      setNewFiles([])
      setReusedAssets([])
      setMarkup(null)
      setButtonsResetKey((key) => key + 1)
      setCampaignIds([])
      setOrderIds([])
      setDraftErrors(null)
      setMarkupError(null)
      setProgress(null)
      setSubmitMessage(null)
      setPickerOpen(false)
    }
  }, [open])

  useEffect(() => {
    if (focusMarkupRef.current && markupError) {
      focusMarkupRef.current = false
      markupSectionRef.current?.scrollIntoView({ block: "center" })
      markupSectionRef.current?.focus()
    }
  }, [markupError])

  const removeNewFile = (index: number) =>
    setNewFiles((prev) => prev.filter((_, i) => i !== index))

  const removeReusedAsset = (id: number) =>
    setReusedAssets((prev) => prev.filter((asset) => asset.id !== id))

  const addPickedAssets = (picked: IPickedAsset[]) =>
    setReusedAssets((prev) => {
      const known = new Set(prev.map((asset) => asset.id))
      return [...prev, ...picked.filter((asset) => !known.has(asset.id))]
    })

  const handleMarkupChange = (next?: {
    inline_keyboard: Array<
      Array<{
        text: string
        url: string
        style?: "danger" | "success" | "primary"
      }>
    >
  }) =>
    setMarkup(
      next?.inline_keyboard && next.inline_keyboard.length > 0
        ? {
            inline_keyboard: next.inline_keyboard.map((row) =>
              row.map(({ text: buttonText, url, style }) => ({
                text: buttonText,
                url,
                style,
              })),
            ),
          }
        : null,
    )

  const handleCreateError = (err: unknown) => {
    const shape = normalizeContentError(err)
    const code = shape.code
    if (code === "unknown_assets") {
      setReusedAssets([])
      setSubmitMessage(contentErrorMessage({ ...shape, code }))
      return
    }
    if (code === "unknown_campaigns") {
      setCampaignIds([])
      setSubmitMessage(contentErrorMessage({ ...shape, code }))
      return
    }
    if (code === "unknown_orders") {
      setOrderIds([])
      setSubmitMessage(contentErrorMessage({ ...shape, code }))
      return
    }
    if (code === "invalid_reply_markup") {
      focusMarkupRef.current = true
      setMarkupError(contentErrorMessage({ ...shape, code }))
      return
    }
    setSubmitMessage(contentErrorMessage(shape))
  }

  const submit = async () => {
    if (!canWriteRef.current || abortRef.current) return
    const collected = buttonsRef.current?.validateAndCollect()
    if (!collected?.ok) return
    const currentMarkup = collected.markup

    const errors = validateContentDraft({
      text,
      newFiles,
      reusedAssets: reusedAssets.map(({ id, sizeBytes }) => ({
        id,
        sizeBytes,
      })),
      replyMarkup: currentMarkup,
    })
    const currentMarkupError = validateReplyMarkup(currentMarkup)
    setDraftErrors(errors)
    setMarkupError(currentMarkupError)
    setSubmitMessage(null)
    if (hasContentDraftErrors(errors) || currentMarkupError) {
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    setProgress(0)
    try {
      await createContent({
        body: {
          text: text.trim() !== "" ? text : undefined,
          files: newFiles.length > 0 ? newFiles : undefined,
          assetIds:
            reusedAssets.length > 0 ? reusedAssets.map((a) => a.id) : undefined,
          replyMarkup: currentMarkup ?? undefined,
          campaignIds: campaignIds.length > 0 ? campaignIds : undefined,
          orderIds: resolvedOrderIds.length > 0 ? resolvedOrderIds : undefined,
        },
        onUploadProgress: (pct) => setProgress(pct),
        signal: controller.signal,
      }).unwrap()
      enqueueSnackbar("Материал создан", { variant: "success" })
      onClose()
    } catch (err) {
      handleCreateError(err)
    } finally {
      abortRef.current = null
      setProgress(null)
    }
  }

  const cancel = () => {
    if (abortRef.current) {
      // FR-18: abort без авторетрая; сообщение покажет handleCreateError (код aborted).
      abortRef.current.abort()
      return
    }
    onClose()
  }

  const dismiss = () => {
    if (!abortRef.current) onClose()
  }

  const textCounter = Array.from(text).length

  return (
    <>
      <Modal
        title="Создать материал"
        open={open}
        onClose={isSubmitting ? undefined : dismiss}
        disableBackdropClose={isSubmitting}
        dialogProps={{ maxWidth: "md" }}
        actions={[
          <Button key="cancel" onClick={cancel}>
            Отмена
          </Button>,
          <Button
            key="submit"
            variant="contained"
            disabled={isSubmitting || !canWrite}
            onClick={submit}
          >
            Создать материал
          </Button>,
        ]}
      >
        <Stack
          component="fieldset"
          disabled={!canWrite}
          spacing={2}
          sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
        >
          {isSubmitting && progress !== null && (
            <Stack spacing={0.5}>
              <LinearProgress
                variant={progress >= 100 ? "indeterminate" : "determinate"}
                value={progress >= 100 ? undefined : progress}
              />
              <Typography variant="caption">
                {progress >= 100 ? "Сохранение…" : `${progress} %`}
              </Typography>
            </Stack>
          )}
          {submitMessage && <Alert severity="error">{submitMessage}</Alert>}
          <TextField
            label="Текст материала"
            multiline
            minRows={3}
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            error={draftErrors?.text !== undefined}
            helperText={draftErrors?.text ?? `${textCounter} / 100000 символов`}
          />
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body1">Файлы:</Typography>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => fileInputRef.current?.click()}
              >
                Добавить файлы
              </Button>
              <Button size="small" onClick={() => setPickerOpen(true)}>
                Выбрать из существующих
              </Button>
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={CONTENT_ACCEPT}
              hidden
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? [])
                setNewFiles((prev) => [...prev, ...picked])
                e.target.value = ""
              }}
            />
            {draftErrors?.files.map((fileError) => (
              <FormHelperText error key={fileError}>
                {fileError}
              </FormHelperText>
            ))}
            {draftErrors?.total && (
              <FormHelperText error>{draftErrors.total}</FormHelperText>
            )}
            {draftErrors?.empty && (
              <FormHelperText error>{draftErrors.empty}</FormHelperText>
            )}
            {(newFiles.length > 0 || reusedAssets.length > 0) && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {newFiles.map((file, index) => (
                  <Chip
                    key={`${file.name}_${index}`}
                    size="small"
                    label={`${index + 1}. ${file.name}`}
                    disabled={!canWrite}
                    onDelete={() => removeNewFile(index)}
                    deleteIcon={<DeleteIcon />}
                  />
                ))}
                {reusedAssets.map((asset, index) => (
                  <Chip
                    key={asset.id}
                    size="small"
                    variant="outlined"
                    label={`${newFiles.length + index + 1}. ${asset.label} (${formatBytes(asset.sizeBytes)})`}
                    disabled={!canWrite}
                    onDelete={() => removeReusedAsset(asset.id)}
                    deleteIcon={<Close />}
                  />
                ))}
              </Stack>
            )}
          </Stack>
          <Box ref={markupSectionRef} tabIndex={-1}>
            <Stack spacing={1}>
              {markupError && (
                <FormHelperText error role="alert">
                  {markupError}
                </FormHelperText>
              )}
              <UrlButtons
                ref={buttonsRef}
                resetKey={buttonsResetKey}
                data={{
                  inline_keyboard:
                    markup?.inline_keyboard.map((row) =>
                      row.map((button) => ({ ...button })),
                    ) ?? [],
                }}
                onChange={handleMarkupChange}
                hideIconField
                validateFields={validateContentButtonFields}
                validateLayout={validateReplyMarkupLayout}
              />
            </Stack>
          </Box>
          <Stack spacing={1}>
            <Typography variant="body1">Привязки:</Typography>
            {presetOrderBinding !== undefined && (
              <Chip size="small" label={`Ордер #${presetOrderBinding}`} />
            )}
            <AutocompleteMultipleCampaigns
              campaigns={campaigns ?? []}
              value={campaignIds}
              onChange={setCampaignIds}
              disabled={!canWrite}
            />
            {presetOrderBinding === undefined && (
              <OrderPickerField value={orderIds} onChange={setOrderIds} />
            )}
          </Stack>
          {/* Условный маунт обязателен: useInfiniteSelectList не имеет skip —
              при безусловном рендере первая страница /contents грузилась бы
              сразу при открытии формы создания. */}
          {pickerOpen && canWrite && (
            <AssetPickerDialog
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              onSubmit={addPickedAssets}
              reservedSlots={newFiles.length}
              existingAssetIds={reusedAssets.map(({ id }) => id)}
            />
          )}
        </Stack>
      </Modal>
    </>
  )
}
